/**
 * Bill of Lading Equipment Fields Component
 *
 * Renders the utilized transport equipments section of the B/L form.
 * Unlike OBEquipmentFields (which has simple commodity sub-items),
 * B/L equipment entries include:
 * - Container reference, ISO code, shipper-owned flag
 * - Weight row: tare weight, gross weight, verified gross mass
 * - Seals: nested dynamic array (sealNumber, sealSource, sealType)
 * - Active reefer settings: collapsible section for temperature/humidity/ventilation
 *
 * Uses useFieldArray for both the top-level equipment array and nested seals.
 */

import { useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { getFieldIssues } from '../../utils/confidenceUtils';

const EQUIPMENT_CODES = [
  { value: '', label: 'Select...' },
  { value: '22G1', label: '22G1 - 20ft Dry' },
  { value: '42G1', label: '42G1 - 40ft Dry' },
  { value: '45G1', label: '45G1 - 40ft High Cube Dry' },
  { value: '22R1', label: '22R1 - 20ft Reefer' },
  { value: '42R1', label: '42R1 - 40ft Reefer' },
  { value: '45R1', label: '45R1 - 40ft High Cube Reefer' },
  { value: '22U1', label: '22U1 - 20ft Open Top' },
  { value: '42U1', label: '42U1 - 40ft Open Top' },
  { value: '22P1', label: '22P1 - 20ft Flat Rack' },
  { value: '42P1', label: '42P1 - 40ft Flat Rack' },
  { value: '22T1', label: '22T1 - 20ft Tank' },
  { value: '42T1', label: '42T1 - 40ft Tank' }
];

const WEIGHT_UNITS = [
  { value: 'KGS', label: 'KGS' },
  { value: 'LBS', label: 'LBS' }
];

const TEMP_UNITS = [
  { value: 'CEL', label: 'CEL - Celsius' },
  { value: 'FAH', label: 'FAH - Fahrenheit' }
];

const SEAL_SOURCE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'CAR', label: 'CAR - Carrier' },
  { value: 'SHI', label: 'SHI - Shipper' },
  { value: 'PHY', label: 'PHY - Phytosanitary' },
  { value: 'VET', label: 'VET - Veterinary' },
  { value: 'CUS', label: 'CUS - Customs' }
];

const SEAL_TYPE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'KLP', label: 'KLP - Keyless Padlock' },
  { value: 'BLT', label: 'BLT - Bolt' },
  { value: 'CBT', label: 'CBT - Cable Tie' },
  { value: 'LKD', label: 'LKD - Locked' },
  { value: 'WIR', label: 'WIR - Wire' }
];

// ---------------------------------------------------------------------------
// Seals nested array
// ---------------------------------------------------------------------------

/**
 * Renders the seals[] nested dynamic array for one equipment entry.
 */
function SealsSection({ equipmentIndex, control, register, isEditMode, issues }) {
  const [isOpen, setIsOpen] = useState(false);
  const basePath = `utilizedTransportEquipment.${equipmentIndex}.seals`;
  const baseIssuePath = `utilizedTransportEquipment[${equipmentIndex}].seals`;

  const { fields, append, remove } = useFieldArray({ control, name: basePath });

  const addSeal = () => {
    append({ sealNumber: '', sealSource: '', sealType: '' });
  };

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800"
        >
          <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Seals ({fields.length})
        </button>
        {isEditMode && isOpen && (
          <Button variant="secondary" size="sm" onClick={addSeal}>
            + Add Seal
          </Button>
        )}
      </div>

      {isOpen && (
        <div className="space-y-2 ml-4">
          {fields.length === 0 && (
            <div className="text-center py-2 text-gray-400 text-sm bg-gray-50 rounded">
              No seals specified.
            </div>
          )}
          {fields.map((seal, sIdx) => (
            <div key={seal.id} className="flex items-end gap-2">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input
                  label="Seal Number"
                  {...register(`${basePath}.${sIdx}.sealNumber`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${sIdx}].sealNumber`)}
                  placeholder="e.g., SEAL123456"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Seal Source</label>
                  <select
                    {...register(`${basePath}.${sIdx}.sealSource`)}
                    disabled={!isEditMode}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
                  >
                    {SEAL_SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Seal Type</label>
                  <select
                    {...register(`${basePath}.${sIdx}.sealType`)}
                    disabled={!isEditMode}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
                  >
                    {SEAL_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              {isEditMode && (
                <button type="button" onClick={() => remove(sIdx)} className="text-red-500 hover:text-red-700 text-sm mb-4">
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active Reefer Settings collapsible section
// ---------------------------------------------------------------------------

/**
 * Collapsible reefer settings section — only relevant for reefer containers.
 */
function ReeferSection({ equipmentIndex, register, isEditMode, issues }) {
  const [isOpen, setIsOpen] = useState(false);
  const basePath = `utilizedTransportEquipment.${equipmentIndex}.activeReeferSettings`;
  const baseIssuePath = `utilizedTransportEquipment[${equipmentIndex}].activeReeferSettings`;

  return (
    <div className="border-t pt-3 mt-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800 mb-2"
      >
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Reefer Settings (optional)
      </button>

      {isOpen && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 ml-4">
          <Input
            label="Temperature Setpoint"
            type="number"
            step="0.1"
            {...register(`${basePath}.temperatureSetpoint`, { valueAsNumber: true })}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, `${baseIssuePath}.temperatureSetpoint`)}
            placeholder="e.g., -18"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temperature Unit</label>
            <select
              {...register(`${basePath}.temperatureUnit`)}
              disabled={!isEditMode}
              className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
            >
              {TEMP_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>

          <Input
            label="Humidity Setpoint (%)"
            type="number"
            step="0.1"
            {...register(`${basePath}.humiditySetpoint`, { valueAsNumber: true })}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, `${baseIssuePath}.humiditySetpoint`)}
            placeholder="e.g., 85"
          />

          <Input
            label="Ventilation Setpoint (m³/h)"
            type="number"
            step="0.1"
            {...register(`${basePath}.ventilationSetpoint`, { valueAsNumber: true })}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, `${baseIssuePath}.ventilationSetpoint`)}
          />

          {/* Boolean checkboxes */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register(`${basePath}.isGeneratorSetRequired`)}
              disabled={!isEditMode}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              id={`genset-${equipmentIndex}`}
            />
            <label htmlFor={`genset-${equipmentIndex}`} className="text-sm text-gray-700 cursor-pointer">
              Generator Set Required
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register(`${basePath}.isColdTreatmentRequired`)}
              disabled={!isEditMode}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              id={`cold-treatment-${equipmentIndex}`}
            />
            <label htmlFor={`cold-treatment-${equipmentIndex}`} className="text-sm text-gray-700 cursor-pointer">
              Cold Treatment Required
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * BLEquipmentFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {Function} props.watch - react-hook-form watch
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function BLEquipmentFields({ control, register, watch, isEditMode, issues = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'utilizedTransportEquipment'
  });

  const addEquipment = () => {
    append({
      equipmentReference: '',
      ISOEquipmentCode: '',
      isShipperOwned: false,
      tareWeight: null,
      tareWeightUnit: 'KGS',
      grossWeight: null,
      grossWeightUnit: 'KGS',
      verifiedGrossMass: null,
      verifiedGrossMassUnit: 'KGS',
      seals: [],
      activeReeferSettings: {
        temperatureSetpoint: null,
        temperatureUnit: 'CEL',
        humiditySetpoint: null,
        ventilationSetpoint: null,
        isGeneratorSetRequired: false,
        isColdTreatmentRequired: false
      }
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">Equipment</h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addEquipment}>
            + Add Equipment
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No equipment added yet. Click "Add Equipment" to specify containers.
        </div>
      )}

      {/* Equipment entries */}
      {fields.map((field, index) => (
        <div key={field.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
          {/* Equipment header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Equipment #{index + 1}
              {field.equipmentReference && ` — ${field.equipmentReference}`}
            </span>
            {isEditMode && (
              <button type="button" onClick={() => remove(index)} className="text-red-500 hover:text-red-700 text-sm">
                Remove
              </button>
            )}
          </div>

          {/* Core fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input
              label="Equipment Reference (Container No.)"
              {...register(`utilizedTransportEquipment.${index}.equipmentReference`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `utilizedTransportEquipment[${index}].equipmentReference`)}
              placeholder="e.g., MSCU1234567"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ISO Equipment Code</label>
              <select
                {...register(`utilizedTransportEquipment.${index}.ISOEquipmentCode`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {EQUIPMENT_CODES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            {/* Shipper Owned checkbox */}
            <div className="flex items-end pb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register(`utilizedTransportEquipment.${index}.isShipperOwned`)}
                  disabled={!isEditMode}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Shipper Owned Container (SOC)</span>
              </label>
            </div>
          </div>

          {/* Weight row: tare / gross / VGM */}
          <div className="border-t pt-3">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Weights</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Input
                label="Tare Weight"
                type="number"
                {...register(`utilizedTransportEquipment.${index}.tareWeight`, { valueAsNumber: true })}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `utilizedTransportEquipment[${index}].tareWeight`)}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <select
                  {...register(`utilizedTransportEquipment.${index}.tareWeightUnit`)}
                  disabled={!isEditMode}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
                >
                  {WEIGHT_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>

              <Input
                label="Gross Weight"
                type="number"
                {...register(`utilizedTransportEquipment.${index}.grossWeight`, { valueAsNumber: true })}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `utilizedTransportEquipment[${index}].grossWeight`)}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <select
                  {...register(`utilizedTransportEquipment.${index}.grossWeightUnit`)}
                  disabled={!isEditMode}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
                >
                  {WEIGHT_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>

              <Input
                label="Verified Gross Mass"
                type="number"
                {...register(`utilizedTransportEquipment.${index}.verifiedGrossMass`, { valueAsNumber: true })}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `utilizedTransportEquipment[${index}].verifiedGrossMass`)}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <select
                  {...register(`utilizedTransportEquipment.${index}.verifiedGrossMassUnit`)}
                  disabled={!isEditMode}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
                >
                  {WEIGHT_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Seals */}
          <SealsSection
            equipmentIndex={index}
            control={control}
            register={register}
            isEditMode={isEditMode}
            issues={issues}
          />

          {/* Reefer Settings */}
          <ReeferSection
            equipmentIndex={index}
            register={register}
            isEditMode={isEditMode}
            issues={issues}
          />
        </div>
      ))}
    </div>
  );
}

export default BLEquipmentFields;
